/* eslint-disable @typescript-eslint/no-explicit-any, complexity */
import { isEmpty } from 'lodash';
import { Store } from 'redux';

import { WORKSPACES_AND_ORGANISATION_BASE } from '../app-core/constants';
import { Logger } from '../logger/logger';
import { matchConsolidationsRoutePath, organisationByIdSelector } from '../organisations';
import { getParamsByPath } from '../path';
import { isUUID } from '../validators';
import { workspaceByIdSelector } from '../workspaces';
import { TrackerType, CustomProperties, NoopTracker, UserWithHashPayload } from './types';
import { TRACKER_DISABLED } from './constants';

export interface WindowWithSegment extends Window {
  analytics?: any;
  mixpanel?: any;
  Intercom?: any;
}

export class Tracker {
  private intercomAppID: string | undefined;
  private reduxStore: Store | undefined;
  private isEnabled = false;
  private noopTracker: NoopTracker = {
    identify: Logger.info,
    track: Logger.info,
    page: Logger.info,
    log: Logger.info,
    reset: Logger.info,
    alias: Logger.info,
    resetIntercom: Logger.info
  };
  private segmentId: string;

  constructor(segmentId: string, intercomAppID?: string, reduxStore?: Store) {
    this.reduxStore = reduxStore;
    this.segmentId = segmentId;
    this.intercomAppID = intercomAppID;
    this.initAnalytics();
  }

  public initAnalytics = () => {
    const analytics = this.getAnalytics();

    if (!analytics) {
      Logger.error('Segment snippet has not been loaded');
      return;
    }

    // Loads the analytics only if it hasn't already been done
    if (!analytics.initialize) {
      analytics.load(this.segmentId);
      this.isEnabled = true;
    }
  };

  public track: TrackerType['track'] = (event, properties, options, callback) => {
    if (!this.isEnabled) {
      return this.noopTracker.track(TRACKER_DISABLED);
    }

    const customProperties = { ...properties, ...this.getCustomProperties(this.reduxStore) };

    this.getAnalytics().track(event, customProperties, options, callback);
  };

  public page: TrackerType['page'] = (category, name, properties, options, callback) => {
    if (!this.isEnabled) {
      return this.noopTracker.page(TRACKER_DISABLED);
    }

    const customProperties = { ...properties, ...this.getCustomProperties(this.reduxStore) };

    const analytics = this.getAnalytics();

    analytics.track(name ? `Visited Page: ${name}` : undefined, customProperties, {
      ...options,
      integrations: {
        All: false,
        Userpilot: true,
        Intercom: true
      }
    });
    analytics.page(category, name, customProperties, options, callback);
  };

  public identify: TrackerType['identify'] = (...args) => {
    if (!this.isEnabled) {
      return this.noopTracker.identify(TRACKER_DISABLED);
    }

    this.getAnalytics().identify(...args);
  };

  public alias: TrackerType['alias'] = (...args) => {
    if (!this.isEnabled) {
      return this.noopTracker.alias(TRACKER_DISABLED);
    }

    this.getAnalytics().alias(...args);
  };

  public reset: TrackerType['reset'] = (data?: UserWithHashPayload) => {
    if (!this.isEnabled) {
      return this.noopTracker.reset(TRACKER_DISABLED);
    }

    this.getAnalytics().reset();

    const mixpanel = this.getMixpanel();

    if (mixpanel) {
      mixpanel.cookie?.clear();
      mixpanel.reset?.();
    }

    this.resetIntercom(data);
  };

  public resetIntercom: TrackerType['resetIntercom'] = (data?: UserWithHashPayload) => {
    if (!this.isEnabled) {
      return this.noopTracker.resetIntercom(TRACKER_DISABLED);
    }

    const intercom = this.getIntercom();

    if (!intercom || !this.intercomAppID) {
      return;
    }

    intercom('shutdown');

    // After enabling Intercom identity verification we need to add more information to intercom boot
    if (data) {
      const { id, hash, firstName, lastName, email } = data;
      intercom('boot', { app_id: this.intercomAppID, user_id: id, user_hash: hash, firstName, lastName, email });
    }
  };

  public log: TrackerType['log'] = value => Logger.info(value);

  /**
   * Disables the tracker and uses the noop tracker instead of the one with
   * the analytics methods.
   */
  public disable = () => {
    this.isEnabled = false;
  };

  /**
   * Enables the tracker methods, allowing to send the data to analytics.
   */
  public enable = () => {
    const analytics = this.getAnalytics();

    if (analytics && !this.isEnabled) {
      this.isEnabled = true;
    }
  };

  private getAnalytics = () => (window as WindowWithSegment).analytics;
  private getMixpanel = () => (window as WindowWithSegment).mixpanel;
  private getIntercom = () => (window as WindowWithSegment).Intercom;

  private getCustomProperties = (reduxStore?: Store) => {
    // workspace and organisation ids if available
    const params = getParamsByPath(window.location.pathname, WORKSPACES_AND_ORGANISATION_BASE);

    const propertiesWithWorkspaceAndOrg: CustomProperties = {};

    if (!isEmpty(params)) {
      const workspaceId = params.workspaceId;
      if (workspaceId && isUUID(workspaceId)) {
        propertiesWithWorkspaceAndOrg.workspace_id = workspaceId;

        if (reduxStore) {
          const { normalised } = reduxStore.getState();
          const workspace = workspaceByIdSelector(normalised, workspaceId);

          if (workspace) {
            propertiesWithWorkspaceAndOrg.workspace_name = workspace.name;
          }
        }
      }

      const organisationId = params.organisationId;
      if (organisationId && isUUID(organisationId) && !matchConsolidationsRoutePath(window.location.pathname)) {
        if (reduxStore && workspaceId) {
          const { normalised } = reduxStore.getState();
          const org = organisationByIdSelector(normalised, workspaceId, organisationId);

          if (org) {
            propertiesWithWorkspaceAndOrg.organisation_name = org.name;
          }
        }
        propertiesWithWorkspaceAndOrg.organisation_id = organisationId;
      }
    }

    return propertiesWithWorkspaceAndOrg;
  };
}
